import { useEditor, EditorContent } from "@tiptap/react";
import { useEffect } from "react";
import { StarterKit } from "@tiptap/starter-kit";
import { Placeholder } from "@tiptap/extension-placeholder";
import { Link } from "@tiptap/extension-link";
import { Underline } from "@tiptap/extension-underline";
import { Table } from "@tiptap/extension-table";
import { TableRow } from "@tiptap/extension-table-row";
import { TableCell } from "@tiptap/extension-table-cell";
import { TableHeader } from "@tiptap/extension-table-header";
import { Markdown } from "tiptap-markdown";

interface SimpleMarkdownEditorProps {
  content: string;
  onChange: (content: string) => void;
  placeholder?: string;
  highlightText?: string;
  readOnly?: boolean;
}

export function SimpleMarkdownEditor({
  content,
  onChange,
  placeholder = "Type here...",
  highlightText,
  readOnly = false
}: SimpleMarkdownEditorProps) {
  const editor = useEditor({
    editable: !readOnly,
    extensions: [
      StarterKit,
      Underline,
      Markdown.configure({
        html: true,
        tightLists: false,
        bulletListMarker: "-",
        transformPastedText: false,
        transformCopiedText: false,
        breaks: false
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: "underline text-blue-500 hover:text-blue-600 cursor-pointer"
        }
      }),
      Table.configure({
        resizable: true,
        HTMLAttributes: {
          class: "border-collapse w-full my-4"
        }
      }),
      TableRow.configure({
        HTMLAttributes: {
          class: "border-b border-gray-200"
        }
      }),
      TableHeader.configure({
        HTMLAttributes: {
          class: "border-b-2 border-gray-300 bg-gray-100 font-semibold text-left p-2"
        }
      }),
      TableCell.configure({
        HTMLAttributes: {
          class: "border border-gray-200 p-2"
        }
      }),
      Placeholder.configure({
        placeholder
      })
    ],
    content,
    editorProps: {
      attributes: {
        class:
          "prose prose-sm max-w-none focus:outline-hidden min-h-[300px] max-h-[500px] overflow-y-auto px-4 py-3 border border-gray-300 rounded-lg"
      }
    },
    onUpdate: ({ editor }) => {
      const markdown = editor.storage.markdown.getMarkdown();
      onChange(markdown);
    }
  });

  useEffect(() => {
    if (!editor) return;
    editor.setEditable(!readOnly);
  }, [editor, readOnly]);

  useEffect(() => {
    if (!editor) return;

    if (!highlightText) {
      const { from, to } = editor.state.selection;
      if (from !== to) {
        editor.commands.setTextSelection(0);
      }
      return;
    }

    setTimeout(() => {
      if (!editor) return;

      const text = editor.getText().toLowerCase();
      const searchText = highlightText.toLowerCase().trim();
      const pos = text.indexOf(searchText);

      if (pos !== -1) {
        const wasEditable = editor.isEditable;

        if (!wasEditable) {
          editor.setEditable(true);
        }

        editor
          .chain()
          .focus()
          .setTextSelection({ from: pos + 1, to: pos + searchText.length + 1 })
          .run();

        if (!wasEditable) {
          setTimeout(() => {
            editor.setEditable(false);
          }, 100);
        }

        setTimeout(() => {
          const { node } = editor.view.domAtPos(pos + 1);
          if (node && node instanceof Element) {
            node.scrollIntoView({ behavior: "smooth", block: "center" });
          }
        }, 50);
      }
    }, 10);
  }, [editor, highlightText]);

  return (
    <div className={`w-full ${readOnly ? "opacity-75 pointer-events-none" : ""}`}>
      <EditorContent editor={editor} />
    </div>
  );
}
